import { Helmet } from "react-helmet-async";
import { Phone, Mail, MapPin, Clock, CheckCircle } from "lucide-react";
import HubSpotForm from "../components/HubSpotForm";

export default function Contact() {
  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Get a Custom Printing Quote in Dallas TX",
    "description": "Request a free custom screen printing, DTF, or embroidery quote from Ninja Screen Print in Dallas TX. Response within 2 hours.",
    "url": "https://ninjascreenprint.com/contact",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-214-555-0190",
      "contactType": "customer service",
      "email": "orders@ninjascreenprint.com",
      "areaServed": "Dallas, TX",
      "availableLanguage": "English"
    }
  };

  return (
    <>
      <Helmet>
        <title>Get a Free Quote | Custom Screen Printing Dallas TX | Ninja Screen Print</title>
        <meta name="description" content="Request a free custom shirt printing quote in Dallas TX. Screen printing, DTF, embroidery & more. We respond within 2 hours. Free DFW delivery on 24+ pieces." />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
      </Helmet>

      {/* Hero */}
      <div className="pt-16 bg-[#0d0d0d] border-b border-neutral-800">
        <div className="ninja-container py-16">
          <div className="red-line" />
          <h1 className="font-teko text-5xl sm:text-6xl text-white uppercase tracking-tight">
            Get Your Free Quote
          </h1>
          <p className="text-neutral-400 font-inter mt-3 text-lg max-w-xl">
            Submit your order brief and our Dallas team will respond with detailed pricing within 2 business hours. No obligation, no spam.
          </p>
        </div>
      </div>

      <section className="ninja-section">
        <div className="ninja-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2 ninja-card p-8" data-testid="contact-form-wrapper">
              <HubSpotForm />
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              {/* Contact Details */}
              <div className="ninja-card p-6">
                <div className="red-line" />
                <h2 className="font-teko text-2xl text-white uppercase tracking-wide mb-5">Contact Us Directly</h2>
                <div className="space-y-4">
                  <a href="tel:+12145550190" data-testid="contact-phone" className="flex items-start gap-3 group">
                    <div className="w-8 h-8 bg-red-600/10 border border-red-600/30 flex items-center justify-center shrink-0">
                      <Phone size={14} className="text-red-500" />
                    </div>
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-widest font-inter mb-0.5">Phone</div>
                      <div className="text-white font-inter group-hover:text-red-400 transition-colors">(214) 555-0190</div>
                    </div>
                  </a>
                  <a href="mailto:orders@ninjascreenprint.com" data-testid="contact-email" className="flex items-start gap-3 group">
                    <div className="w-8 h-8 bg-red-600/10 border border-red-600/30 flex items-center justify-center shrink-0">
                      <Mail size={14} className="text-red-500" />
                    </div>
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-widest font-inter mb-0.5">Email</div>
                      <div className="text-white font-inter group-hover:text-red-400 transition-colors text-sm">orders@ninjascreenprint.com</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-red-600/10 border border-red-600/30 flex items-center justify-center shrink-0">
                      <MapPin size={14} className="text-red-500" />
                    </div>
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-widest font-inter mb-0.5">Location</div>
                      <div className="text-white font-inter">Dallas, TX</div>
                      <div className="text-neutral-500 font-inter text-xs">Serving all of DFW</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-red-600/10 border border-red-600/30 flex items-center justify-center shrink-0">
                      <Clock size={14} className="text-red-500" />
                    </div>
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-widest font-inter mb-0.5">Business Hours</div>
                      <div className="text-white font-inter text-sm">Mon–Fri: 8:00 AM – 6:00 PM</div>
                      <div className="text-neutral-500 font-inter text-xs">Sat: 9:00 AM – 2:00 PM</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Promise */}
              <div className="ninja-card p-6 bg-red-900/10 border-red-900/30">
                <h3 className="font-teko text-xl text-white uppercase tracking-wide mb-3">Our Response Promise</h3>
                <div className="space-y-2">
                  {[
                    "Quote within 2 business hours",
                    "Digital proof before production",
                    "No production without approval",
                    "Free DFW delivery on 24+ pieces",
                    "Satisfaction guaranteed",
                  ].map(item => (
                    <div key={item} className="flex items-center gap-2 text-sm text-neutral-300 font-inter">
                      <CheckCircle size={12} className="text-red-500 shrink-0" /> {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Popular Orders */}
              <div className="ninja-card p-6">
                <h3 className="font-teko text-xl text-white uppercase tracking-wide mb-3">Popular Orders</h3>
                <div className="space-y-2 text-xs text-neutral-400 font-inter">
                  {[
                    "24 screen-printed event shirts – from $11/pc",
                    "48 corporate polo shirts (embroidered) – from $11/pc",
                    "12 custom jerseys with names & numbers – from $22/pc",
                    "DTF full-color shirts (any quantity) – from $10.50/pc",
                    "300 bulk t-shirts for events – from $7/pc",
                  ].map(item => (
                    <div key={item} className="flex items-start gap-1.5 border-b border-neutral-900 pb-2 last:border-0">
                      <div className="w-1 h-1 bg-red-600 rounded-full mt-1.5 shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
